<template>
  <div
    class="section"
    :class="brandClass()">

    <!-- Sales dealer subsection -->
    <div class="subsection sales-dealer-subsection">

      <!-- Sales dealer header -->
      <OneHead 
        :brandClass="brandClass()" 
        class="dealers-header sales-dealer-header" 
        :isHeader="false">
        <template v-slot:title>{{ $toUpperCase($t("profile_dealers_sales_title")) }}</template>
        <template v-slot:subtitle>{{ $t("profile_dealers_sales_subtitle") }}</template>
      </OneHead>

      <!-- Sales deler content -->
      <div 
        class="dealer-section-card-wrapper dealer-section-sales-card-wrapper" 
        :class="brandClass()">
        <template v-if="!isLoadingDealers">
          <template v-if="preferredSalesDealers.length">

            <!-- For each preferred sales dealer... -->
            <template v-for="(preferredSalesDealer, preferredSalesDealerIndex) in preferredSalesDealers">
              <div
                v-if="!user.dealers[getUserDealerIndex(preferredSalesDealer)].delete"
                :key="preferredSalesDealerIndex"
                class="dealer-section-card-container dealer-section-sales-card-container"
                :class="[
                  brandClass(),
                  { 'dealer-section-card-container-with-margin-bottom' : preferredSalesDealerIndex < (preferredSalesDealers.length - 1) }
                ]">

                <!-- Sales dealer card -->
                <dealer-card 
                  :title="'sales-dealer-card-' + preferredSalesDealerIndex"
                  :dealer="preferredSalesDealer"
                  @onDeleteDealerClick="!editDisabled && openDeleteDealerPopup(getUserDealerIndex(preferredSalesDealer), 'sales', $event)"
                  @onContactDealerButtonClick="openMailTo(preferredSalesDealer.attributes?.mail)"
                  :disabled="!isDealerSalesDealer(preferredSalesDealer)"
                />

                <div 
                  v-if="!isDealerSalesDealer(preferredSalesDealer)" 
                  class="dealer-section-notification-box dealer-section-sales-notification-card" 
                  :key="preferredSalesDealerIndex">
                  <NotificationBox 
                    :messageTextKey="'profile_dealers_unavailable_dealer'" 
                    :type="'warning'" 
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- No sales dealer found -->
          <template v-else>
            <!-- <div 
              class="dealer-section-no-dealer-text-container dealer-section-no-sales-dealer-text-container"
              :class="brandClass()">
              {{ $t('profile_dealers_sales_no_dealer_found') }}
            </div> -->

            <OneButton
              v-if="!isSalesDealersAddListVisible"
              :brandClass="brandClass()"
              :profileStyle="true"
              :type="'secondary'"
              :label="$t('profile_dealers_sales_add_button')"
              @click="onAddDealerClick('sales')"
            />

            <template v-if="isSalesDealersAddListVisible">
              <div class="dealer-add-title sales-dealer-add-title">
                {{$t("profile_dealers_sales_add_title")}}
              </div>

              <div class="add-list-container sales-dealer-add-list-container">
                <div id="load-script">
                  <div id="dealerLocator"></div>
                </div>
              </div>

              <!-- Apply / cancel buttons container -->
              <div class="buttons-container sales-dealer-buttons-container">
                <!-- Apply button -->
                <OneButton
                  :brandClass="brandClass()"
                  :profileStyle="true"
                  :type="'primary'"
                  :label="$t('profile_apply_button')"
                  :disabled="!selectedUserDealer"
                  @click="onApplyButtonClick()"
                />

                <!-- Cancel button -->
                <OneButton
                  :brandClass="brandClass()"
                  :profileStyle="true"
                  :type="'secondary'"
                  :label="$t('profile_cancel_button')"
                  @click="onCancelButtonClick()"
                />
              </div>
            </template>
          </template>
        </template>
      </div>
    </div>

    <!-- Delete dealer popup -->
    <OneAlert
      :brandClass="brandClass()"
      v-if="isDeleteDealerPopupOpen"
      :title="$t('profile_dealers_delete_popup_title')"
      :content="$t('profile_dealers_delete_popup_subtitle')"
      :button_confirm_label="$t('profile_delete_button')"
      :button_cancel_label="$t('profile_cancel_button')"
      @confirm="deleteDealer"
      @close="closeDeleteDealerPopup"
    />
  </div>
</template>

<script>
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import VueJsonPretty from "vue-json-pretty";

import DealerCard from '../../commons/DealerCard.vue';
import NotificationBox from "@/components/ProfileForm/components/NotificationBox";

export default {
  inject:['$c', '$t', 'brandClass', '$toUpperCase'],
  components: {
    VueJsonPretty,
    DealerCard,
    NotificationBox
  },
  name: "DealersSection",
  props: {
    user: {},
    editDisabled: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    dealers: [],
    isLoadingDealers: false,
    clickedDeleteButtonId: null,
    dealerTypeToDelete: null,
    isDeleteDealerPopupOpen: false,
    indexOfUserDealerToDelete: null,
    dealerTypeToAdd: null,
    selectedHttpDealer: null,
    selectedUserDealer: null,
    indexOfDealerToAdd: null,
    isSalesDealersAddListVisible: false,
  }),
  computed: {
    /**
     * Return the current selected brand as string
     */
    brand() {
      const brand = this.$store.state.configurationParams.brand || oneUtils.properties.DEFAULT_FALLBACK_BRAND;

      switch(brand) {
        case "mini": return "MINI"
        case "rr": return "RR"
        case "alphabet": return "ALPHABET"
        case "alphera": return "ALPHERA"
        case "bmwmotorrad": return "BMW_MOTORRAD"
        case "toyota": return "TOYOTA"
        case "bmw":
        default: return "BMW"
      }
    },
    brandForDloaas() {
      switch(this.brand) {
        case "MINI": return "MINI"
        case "BMW_MOTORRAD": return "BMW_BMWM"
        case "BMW": 
        default: return "BMW"
      }
    },
    /**
     * Returns the list of dealers saved in the store
     */
    getStoredDealers() {
      return this.$store.getters.getDealers;
    },
    /**
     * Return the list of the user's preferred sales dealers
     */
    userSalesDealers() {
      return this.user.dealers?.filter(userDealer => userDealer.isPreferredSalesDealer);
    },
    /**
     * Returns the preferred sales dealers array: a sales dealer must be part of the "F" or "G" distribution branch
     * 
     * @uses isDealerEqualToUserDealer()
     */
    preferredSalesDealers() {
      let preferredSalesDealers = [];

      this.userSalesDealers?.forEach(userDealer => {
        let dealer = this.dealers?.find(dealer => 
          this.isDealerEqualToUserDealer(dealer, userDealer) &&
          this.isDealerMatchingTheBrand(dealer)
        );

        if(dealer) {
          preferredSalesDealers.push(dealer);
        }
        else if(this.isUserDealerMatchingTheBrand(userDealer)) {
          preferredSalesDealers.push(userDealer);
        }
      });

      return preferredSalesDealers;
    },
  },
  mounted() {
    oneUtils.utils.loadScript(this.$c("api_url_dloaas"), "module").then(() => {
      oneUtils.utils.hideGlobalLoader();
      this.getDealers();
    });
  },
  unmounted() {
    this.removeEventListeners();
    this.resetAddDealerVariables();
  },
  methods: {
    /**
     * Returns true if the provided dealer category corresponds to the current selected brand, false otherwise
     */
    isDealerMatchingTheBrand(dealer) {
      return this.brand == dealer.category;
    },
    /**
     * Returns true if the provided userDealer brand corresponds to the current selected brand, false otherwise
     */
    isUserDealerMatchingTheBrand(userDealer) {
      return this.brand == userDealer.brand;
    },
    /**
     * Returns true if the fields of provided dealer have the same value of the fields of the provided userDealer.
     * NB: dealer and userDealer objects use a different structure 
     * (dealer is the object coming from the HTTP request, while userDealer is the object stored in the user's account)
     */
    isDealerEqualToUserDealer(dealer, userDealer) {
      // This check is performed when a matching dealer is found on the get dealers API:
      // the BUPA backend does not perform the match with the dealer name to find the corresponding dealer to update, 
      // since it matches only the dealerGuid OR (brand && outletId && distributionPartnerId), 
      // so we won't perform it either here to be aligned logically
      if(dealer.attributes) {
        return (
          userDealer.distributionPartnerId == dealer.attributes.distributionPartnerId && 
          +userDealer.outletId == +dealer.attributes.outletId && 
          // userDealer.dealerName == dealer.name && 
          userDealer.brand == dealer.category
        )
      }
      // If no dealer was found on the get dealers API (the dealer ceased its activity), this check is performed instead
      else {
        return (
          userDealer.distributionPartnerId == dealer.distributionPartnerId && 
          +userDealer.outletId == +dealer.outletId && 
          // userDealer.dealerName == dealer.dealerName && 
          userDealer.brand == dealer.brand
        )
      }
    },
    /**
     * Returns true if the provided dealer is part of the distribution branch "F" or "G", false otherwise
     */
    isDealerSalesDealer(dealer) {
      return (
        dealer.attributes?.distributionBranches.includes("F") || 
        dealer.attributes?.distributionBranches.includes("G")
      );
    },
    /**
     * Given an HTTP dealer, returns the index of the corresponding userDealer.
     * Useful to provide the index of the userDealer to delete
     */
    getUserDealerIndex(dealer) {
      return this.user.dealers?.findIndex(userDealer => this.isDealerEqualToUserDealer(dealer, userDealer));
    },
    /**
     * Performs an HTTP request to retrieve the list of dealers, or recovers them from the application store when HTTP request was previously performed
     */
    getDealers() {
      if(this.getStoredDealers.length) {
        console.log("Dealers found in store: loading data");
        
        this.dealers = this.getStoredDealers;
      }
      else {
        console.log("No dealer stored: perforiming http request");

        this.isLoadingDealers = true;

        oneUtils.httpClient.getDealers(this.$store.state.configurationParams.country, this.$store.state.configurationParams.brand).then(
          (dealers) => {
            // Get list of dealers
            this.dealers = dealers.data.data.pois;

            // De-comment code to log dealers filtered by brand and their distribution branch
            /* console.log("Dealers (T): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("T") && !dealer.attributes.distributionBranches.includes("F") && !dealer.attributes.distributionBranches.includes("G")));
            console.log("Dealers (F): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("F") && !dealer.attributes.distributionBranches.includes("T") && !dealer.attributes.distributionBranches.includes("G")));
            console.log("Dealers (G): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("G") && !dealer.attributes.distributionBranches.includes("F") && !dealer.attributes.distributionBranches.includes("T")));
            console.log("Dealers (T, F): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("T") && dealer.attributes.distributionBranches.includes("F") && !dealer.attributes.distributionBranches.includes("G")));
            console.log("Dealers (T, G): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("T") && dealer.attributes.distributionBranches.includes("G") && !dealer.attributes.distributionBranches.includes("F")));
            console.log("Dealers (F, G): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("F") && dealer.attributes.distributionBranches.includes("G") && !dealer.attributes.distributionBranches.includes("T")));
            console.log("Dealers (T, F, G): ", this.dealers.filter(dealer => this.isDealerMatchingTheBrand(dealer) && dealer.attributes.distributionBranches.includes("T") && dealer.attributes.distributionBranches.includes("F") && dealer.attributes.distributionBranches.includes("G"))); */

            this.isLoadingDealers = false;

            this.$store.dispatch('setupDealers', this.dealers);
          },
          (err) => {
            // Error management
            oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
          }
        );
      }
    },
    /**
     * Function called on add dealer button click
     */
    onAddDealerClick(dealerType) {
      this.dealerTypeToAdd = this.capitalizeFirstLetter(dealerType);

      this["is" + this.dealerTypeToAdd + "DealersAddListVisible"] = true;

      let dloaasConfig = {
        configId: "gcdm_profile_list", 
        country: this.$store.state.configurationParams.country, 
        language: this.$store.state.configurationParams.language, 
        initialBrand: this.brandForDloaas
      };

      console.log("Loading sales config");

      // Sales filters config
      dloaasConfig.brands = [
        {
          brand: this.brandForDloaas,
          filters: [
            {
              name: "newCars",
              selected: true
            },
            {
              name: "usedCars",
              selected: true
            }
          ]
        }
      ];

      // Start DLO
      dloaas?.api.start(dloaasConfig);
      
      // Register to DLO events
      (dloaas?.api).addEventListener('dloaas.api.initialized', this.onDLOaaSApiInitialized);
      (dloaas?.api).addEventListener('dloaas.api.app.initialized', this.onDLOaaSInitialized);
      (dloaas?.api).addEventListener('dloaas.api.app.selectDealer', this.onDLOaaSSelectDealer);
    },
    /**
     * This event is called when the DLO is initialized
     */
    onDLOaaSApiInitialized(event) {
      // console.log("DLOaaS API initialized:", event);
    },
    /**
     * This event is called when the DLO is initialized and ready to be interacted with
     */
    onDLOaaSInitialized(event) {
      // console.log("DLOaaS initialized", event);
    },
    /**
     * This event is called when the user selects a dealer in the DLO
     */
    onDLOaaSSelectDealer(event) {
      this.selectedHttpDealer = event.detail.dealer;
      console.log("DLOaaS select dealer (selectedHttpDealer):", this.selectedHttpDealer);

      this.selectedUserDealer = this.httpDealerToUserDealer(this.selectedHttpDealer);
      console.log("DLOaaS select dealer (selectedUserDealer):", this.selectedUserDealer);

      this.indexOfDealerToAdd = this.getUserDealerIndex(this.selectedHttpDealer);
    },
    /**
     * This function is called when user clicks on apply button when adding a dealer
     */
    onApplyButtonClick() {
      if(this.indexOfDealerToAdd && this.indexOfDealerToAdd != -1) {
        this.user.dealers[this.indexOfDealerToAdd].dealerName = this.selectedUserDealer.dealerName;
        this.user.dealers[this.indexOfDealerToAdd]["isPreferred" + this.dealerTypeToAdd + "Dealer"] = true;
      }
      else {
        if(!this.user.dealers) {
          this.user.dealers = [];
        }

        this.selectedUserDealer["isPreferred" + this.dealerTypeToAdd + "Dealer"] = true;

        this.user.dealers.push(this.selectedUserDealer);
      }

      this.removeEventListeners();
      this.resetAddDealerVariables();

      // Refreshes data
      this.$emit('autosave');
    },
    /**
     * This function is called when user clicks on cancel button when adding a dealer
     */
    onCancelButtonClick() {
      this.removeEventListeners();
      this.resetAddDealerVariables();
    },
    /**
     * This functions resets the variables used to add a dealer
     */
    resetAddDealerVariables() {
      this.selectedHttpDealer = null;
      this.selectedUserDealer = null;
      this.indexOfDealerToAdd = null;
      this.isSalesDealersAddListVisible = false;
    },
    /**
     * Converts and returns the provided httpDealer to the user.dealer format
     */
    httpDealerToUserDealer(httpDealer) {
      return {
        "dealerName": httpDealer.name,
        "distributionPartnerId": httpDealer.attributes.distributionPartnerId,
        "outletId": httpDealer.attributes.outletId,
        "brand": httpDealer.category,
        "isPreferredSalesDealer": false,
        "isPreferredServiceDealer": false
      };
    },
    /**
     * Remove DLO event listeners
     */
    removeEventListeners() {
      dloaas?.api.stop();
      (dloaas?.api).removeEventListener('dloaas.api.initialized', this.onDLOaaSApiInitialized);
      (dloaas?.api).removeEventListener('dloaas.api.app.initialized', this.onDLOaaSInitialized);
      (dloaas?.api).removeEventListener('dloaas.api.app.selectDealer', this.onDLOaaSSelectDealer);
    },
    /**
     * Shows the delete dealer popup and sets the provieded index to this.indexOfUserDealerToDelete
     */
    openDeleteDealerPopup(indexOfDealerToDelete, dealerType, clickedDeleteButtonId) {
      this.isDeleteDealerPopupOpen = true;
      this.indexOfUserDealerToDelete = indexOfDealerToDelete;
      this.dealerTypeToDelete = dealerType;
      this.clickedDeleteButtonId = clickedDeleteButtonId;
    },
    /**
     * Closes the delete dealer popup and sets to null this.indexOfUserDealerToDelete
     */
    closeDeleteDealerPopup() {
      this.isDeleteDealerPopupOpen = false;
      this.indexOfUserDealerToDelete = null;
      this.dealerTypeToDelete = null;
      document.getElementById(this.clickedDeleteButtonId)?.focus();
      this.clickedDeleteButtonId = null;
    },
    /**
     * Deletes the user dealer associated to the index specified in this.indexOfUserDealerToDelete
     */
    deleteDealer() {
      // Deletion logic

      this.user.dealers[this.indexOfUserDealerToDelete]["isPreferred" + this.capitalizeFirstLetter(this.dealerTypeToDelete) + "Dealer"] = false;

      // Dealers global cleanup
      this.user.dealers.forEach(userDealer => {
        if(
          !userDealer.isPreferredSalesDealer &&
          !userDealer.isPreferredServiceDealer
        ) {
          userDealer.delete = true;
        }
      });

      this.closeDeleteDealerPopup();

      oneUtils.utils.showGlobalLoader();
      oneUtils.httpClient.updateUser(window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
        .then((data) => {
          oneUtils.utils.hideGlobalLoader();
          oneUtils.utils.showMessage("SUCCESS", this.$t("profile_dealers_success"));
        })
        .catch((e) => {
          oneUtils.utils.hideGlobalLoader();
          oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_dealers"));
        });
    },
    /**
     * Creates, opens and then deletes an <a> tag with href=mailto to the provided email
     */
    openMailTo(email) {
      const element = document.createElement('a');
      element.setAttribute('href', 'mailto:' + email);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();

      // Clean the created element from HTML once used
      element.remove();
    },
    /**
     * Capitalizes first letter of the provided string and returns the changed string
     */
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
  }
};
</script>

<style lang="scss" scoped>
  .__m {
    .dealers-header {
      margin-bottom: 1rem;

      .dealers-header-title {
        font-size: 22px;
        line-height: 22px;

        @media screen and (min-width: 769px) {
          font-size: 32px;
          line-height: 32px;
        }
      }
    }

    .dealer-section-card-container {
      &.dealer-section-card-container-with-margin-bottom {
        margin-bottom: 2rem;
      }
    }

    .dealer-section-notification-box {
      margin-top: 1rem;
    }

    .dealer-section-no-dealer-text-container {
      font-size: 16px;
    }

    .sales-dealer-subsection {
      margin-bottom: 4rem;
    }

    .dealer-add-title {
      font-size: 16px;
      margin-bottom: 1rem;
    }

    .add-list-container {
      margin-bottom: 1rem;
    }

    .buttons-container {
      display: flex;

      .custom-button {
        width: 50%;
        
        &.primary {
          margin-right: 0.5rem;
        }

        &.secondary {
          margin-left: 0.5rem
        }
      }
    }
  }
</style>
